import React, { useEffect, useRef, useState } from "react";
import { Col, Row } from "antd";
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalBasicLayout } from "@vibe/core/next";
import Spinner from "../Spinner";
import WorkspacesAndBoardsList from "./WorkspacesAndBoardsList";
import useUser from "../../hooks/context/userHook";
import useBoardAndItems from "../../hooks/context/useBoardAndItems";
import { useList } from "../../hooks/ui";
import useTokenHook from "../../hooks/context/useTokenHook";
import { useCheckSubitems } from "../../hooks/api/useCheckSubitems";
import { useCheckUpdates } from "../../hooks/api/useCheckUpdates";
import logger from "../../lib/logger";
import { addNewItem } from "./AddItemUtils";

const AddItem = ({
  calcHeight,
  batchSize = 50,
  disableAll,
  moreThan20,
  onGroupSelect,
}) => {
  const { userId, accountId } = useUser();
  const { token } = useTokenHook();

  const {
    isLoading: itemsLoader,
    setItemLoading,
    setItemDisabled,
    isDisabled: itemsDisabled,
    isDisableAll,
    removeItemDisabled,
    removeItemLoading,
  } = useList();

  const { currentBoard, itemIds } = useBoardAndItems();
  const [newItemLoadingState] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const [previousHeight, setPreviousHeight] = useState(0);
  const smallAccountContainer = useRef(null);
  const { processSubitems, setProcessSubitems } = useCheckSubitems();

  const { processUpdates, setProcessUpdates } = useCheckUpdates();

  useEffect(() => {
    calcHeight(smallAccountContainer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (moreThan20 && processSubitems) {
      setModalOpen(true);
    }
  }, [moreThan20, processSubitems]);

  useEffect(() => {
    const checkHeightChange = () => {
      if (smallAccountContainer.current) {
        const currentHeight = smallAccountContainer.current.offsetHeight;

        if (smallAccountContainer !== currentHeight) {
          // Height has changed

          setPreviousHeight(currentHeight);

          // Call calcHeight if needed
          calcHeight(smallAccountContainer);
        }
      }
    };

    // Initial check on mount
    checkHeightChange();

    // Set up a MutationObserver to listen for DOM changes
    const observer = new MutationObserver(checkHeightChange);
    if (smallAccountContainer.current) {
      observer.observe(smallAccountContainer.current, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
    }

    // Clean up on unmount
    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousHeight]);

  //this will create a new update and
  const addItemToTargetBoard = async (targetGroupId, targetBoardId) => {
    try {
      setItemLoading(`${targetGroupId}-${targetBoardId}`);
      setItemDisabled(`${targetGroupId}-${targetBoardId}`);

      await addNewItem({
        targetGroupId,
        targetBoardId,
        userId,
        accountId,
        currentBoard,
        itemIds,
        token,
        processSubitems,
        processUpdates,
        batchSize,
        removeItemDisabled,
        removeItemLoading,
      });
    } catch (error) {
      logger.error("Error adding new item to board:", error);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    // Perform search on the initial workspaces
  };

  // Function to close the modal
  const closeModal = () => {
    setModalOpen(false);
  };

  return (
    <div ref={smallAccountContainer}>
      <Row
        style={{
          backgroundColor: "#FFFFFF",
          width: "100%",
        }}
        className="rounded-b add-item-row"
      >
        <Col span={24} className="pb-0 mt-2 mb-0">
          {!newItemLoadingState ? (
            <>
              <WorkspacesAndBoardsList
                searchText={searchText}
                addItem={addItemToTargetBoard}
                itemsLoader={itemsLoader}
                itemsDisabled={itemsDisabled}
                isDisableAll={isDisableAll}
                handleSearch={handleSearch}
                processUpdates={processUpdates}
                setProcessUpdates={setProcessUpdates}
                processSubitems={processSubitems}
                setProcessSubitems={setProcessSubitems}
                onGroupSelect={onGroupSelect}
              />
            </>
          ) : (
            <>
              <div className="flex flex-col gap-2 items-center">
                <Spinner className="mb-2" />
                <span>
                  Adding your item to the new board. Please do not close this window and the item
                  view.
                </span>
              </div>
            </>
          )}
        </Col>
      </Row>

      {modalOpen && (
        <Modal id="story-book-modal" onClose={closeModal} show={modalOpen} size="medium">
          <ModalBasicLayout>
            <ModalHeader title="Limit reached." className="px-8" />
            <ModalContent>
              <p>
                One or more of your selected items have more than 20 subitems. Please deselect this
                item or do not include subitems when adding to another board. Otherwise, some items
                and subitems will not be processed.
              </p>
            </ModalContent>
          </ModalBasicLayout>
          <ModalFooter
            primaryButton={{
              onClick: () => {
                closeModal();
              },
              text: "Close",
            }}
          />
        </Modal>
      )}
    </div>
  );
};

export default AddItem;
